// @givmo/app-charity — Organization search (CPA-WEB2, plan A-1 entry).
//
// The claim entry point: a charity admin searches Givmo's catalog by NAME or EIN and picks the org to
// claim. Wiring:
//   - SEARCH → `charitiesGetCharities` (GET /charities?q=…) — the existing catalog keyword search
//     (name / EIN), paginated. The api-client envelope middleware unwraps `{data,meta,links}` to a
//     bare `CharityOut[]`; `extractCharities` decodes it tolerantly. A short debounce keeps keystroke
//     traffic down; an empty query shows the welcoming zero state (no request fired).
//     ★ It asks for the OPERATOR view (`filter[brand_members]=include`, RIC-VB12): the donor view
//     hides a verified brand's member rows (ALSAC beside St. Jude), and an organization whose row is
//     a member must still be able to find it here — this search is the funnel's only entry.
//   - SELECT → navigate to the claim wizard for that charity (`/claim/$charityId`).
//
// ★ SEARCH AS A WELCOME (CPA-DP1, 2026-08-10). This is the first screen inside the product for a
// caller who has just made an account, and the onboarding chrome exists precisely so it can "feel like
// a welcome" — but the screen it framed was a stock page header over a 280px toolbar search box. The
// rebuild makes it a centred, framed stage in the product's own register:
//   - the search control gets a HERO treatment (styled through the shared component's className seams,
//     not forked) instead of the table-toolbar default it inherited;
//   - there is exactly ONE instruction. The page header, the placeholder, and the idle zero state used
//     to say "search by name or EIN" three times over;
//   - the idle state previews the PATH (the same three steps the auth screens showed) rather than
//     restating the placeholder — the empty screen is where a first-time caller most wants to know
//     what they are in for;
//   - a result row is ONE control. It used to be a card with a button in it, so the row was inert and
//     only the 72px button committed; now the whole row is the commitment and the funnel has exactly
//     one commitment moment, labelled "Continue" rather than "Claim" (claiming is the next screen's
//     act, not this one's).
//
// The root keeps `data-testid="page-claim-search"` (the nav / e2e contract). Each result row carries a
// stable `claim-search-result-{id}` id and the row control keeps `claim-search-select-{id}`.

import { useMemo, useState } from 'react';
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { forwardTargetFor, useMismatchForward } from '@givmo/org-console';
import { sdk, isGivmoError, type CharityOut, type OperatorCharitiesQuery } from '@givmo/api-client';
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  Icon,
  SearchInput,
  Skeleton,
  testIdProp,
} from '@givmo/design-system';
import { Building2, Search, ArrowRight } from 'lucide-react';
import { useDebouncedValue } from '../../lib/use-debounced-value';
import { mismatchForwardDestinations } from '../../lib/env';
import { extractCharities, formatEin } from './claim-decode';
import { wizardPath } from './claim-routes';
import { useActiveOrg } from '../../org/active-org-context';
import { FUNNEL_STEP_FIND, FUNNEL_STEPS, PRODUCT_NAME } from '../../lib/funnel-copy';

const PREFIX = 'claim-search';
const SEARCH_LIMIT = 20;
const MIN_QUERY_LENGTH = 2;

export function OrgSearchPage() {
  const navigate = useNavigate();
  const { membershipsState } = useActiveOrg();
  const [term, setTerm] = useState('');
  const debounced = useDebouncedValue(term.trim(), 300);
  const enabled = debounced.length >= MIN_QUERY_LENGTH;
  // The link-form forward: the memberships the org console reports, matched against the ONE
  // configured destination. Empty destinations here, so this read can never redirect anybody.
  const { memberships } = useMismatchForward({ enabled: true, destinations: {} });
  const crossConsole = useMemo(() => {
    const destinations = mismatchForwardDestinations();
    const target = forwardTargetFor(memberships, destinations);
    if (target === null) return null;
    const membership = memberships.find((row) => {
      const destination = destinations[row.entity_class];
      return typeof destination === 'string' && destination.trim().length > 0;
    });
    return { target, name: membership?.name ?? null };
  }, [memberships]);
  const firstVisit = membershipsState === 'loaded-empty';

  const search = useInfiniteQuery({
    queryKey: ['charity-catalog-search', debounced],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const query: OperatorCharitiesQuery = {
        q: debounced,
        'page[limit]': SEARCH_LIMIT,
        'page[offset]': pageParam,
        'filter[brand_members]': 'include',
      };
      const { data } = await sdk.charitiesGetCharities({ query, throwOnError: true });
      return extractCharities(data);
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.length === SEARCH_LIMIT ? pages.length * SEARCH_LIMIT : undefined,
    enabled,
    placeholderData: keepPreviousData,
  });

  const results: CharityOut[] = useMemo(
    () => (enabled && search.data ? search.data.pages.flat() : []),
    [enabled, search.data],
  );

  const showLoading = enabled && search.isPending;
  const showEmpty = enabled && search.isSuccess && results.length === 0;
  const updatingResults =
    enabled && search.isFetching && !search.isPending && !search.isFetchingNextPage;
  const staleRows = search.isFetching || search.isPlaceholderData;

  // B1: every async transition on this screen is announced once, from ONE region. The visible count
  // line stays silent so a sighted-and-hearing user is not told twice.
  const searchStatus = !enabled
    ? ''
    : showLoading
      ? 'Searching the nonprofit catalog…'
      : search.isError
        ? 'Could not search the catalog.'
        : showEmpty
          ? 'No organizations found.'
          : `${results.length} ${results.length === 1 ? 'organization' : 'organizations'} found.`;

  return (
    <div className="flex w-full justify-center px-24 py-44" {...testIdProp('page-claim-search')}>
      <div className="flex w-full max-w-[680px] flex-col gap-20">
        {/* ── The stage ────────────────────────────────────────────────────────────────────── */}
        <Card className="px-32 py-32">
          <div className="flex flex-col gap-6">
            <h1 className="font-sans text-s24 font-w600 leading-h13 tracking-givmo text-text-strong">
              Find your organization
            </h1>
            {/* THE one instruction on this screen. */}
            <p className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {firstVisit
                ? `Welcome to ${PRODUCT_NAME}. Search Givmo's nonprofit catalog by name or EIN to get started.`
                : "Search Givmo's nonprofit catalog by name or EIN."}
            </p>
          </div>

          <div className="mt-24">
            <SearchInput
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onClear={() => setTerm('')}
              placeholder="Organization name or EIN"
              aria-label="Search organizations by name or EIN"
              containerClassName="w-full"
              className="h-48 rounded-lg pl-36 text-s16"
              testId={`${PREFIX}-input`}
            />
          </div>

          {/* The class-mismatch forward in its LINK form (CPA-WEB40, folded 2026-09-02): a person
              who administers a company has a console of their own, and this door offers it as a
              line rather than redirecting them out from under a page they are reading. Inert today:
              the destination is unset in every charity environment, and the memberships route is
              dark, so the line never renders. */}
          {!enabled && crossConsole !== null && (
            <p
              className="mt-24 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              {...testIdProp(`${PREFIX}-cross-console`)}
            >
              You administer {crossConsole.name ?? 'a company'} on the company console.{' '}
              <a
                href={crossConsole.target}
                className="font-w600 text-brand-700 hover:underline"
                {...testIdProp(`${PREFIX}-cross-console-link`)}
              >
                Open the company console
              </a>
            </p>
          )}
          {/* Idle zero state — the PATH, not a restatement of the placeholder. */}
          {!enabled && (
            <ol
              className="mt-24 flex flex-col gap-12 border-t border-border-subtle pt-24"
              {...testIdProp(`${PREFIX}-idle`)}
            >
              {FUNNEL_STEPS.map((step) => {
                const current = step.n === FUNNEL_STEP_FIND.n;
                return (
                  <li key={step.n} className="flex items-start gap-12">
                    <span
                      className={`mt-2 flex size-20 shrink-0 items-center justify-center rounded-full font-sans text-s12 font-w600 tabular-nums ${
                        current ? 'bg-brand-600 text-white' : 'bg-neutral-100 text-text-secondary'
                      }`}
                      aria-hidden
                    >
                      {step.n}
                    </span>
                    <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                      <span
                        className={
                          current ? 'font-w600 text-text-strong' : 'font-w600 text-text-secondary'
                        }
                      >
                        {step.title}
                        {current ? " — you're here" : ''}
                      </span>
                      {current ? '' : ` — ${step.detail}`}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </Card>

        <p role="status" aria-live="polite" className="sr-only">
          {searchStatus}
        </p>

        {/* ── Loading. Mirrors the loaded row: the same 44px avatar (Avatar `lg`), the same two-line
            block, and the same trailing action, so the list does not re-lay-out on arrival. ─── */}
        {showLoading && (
          <div className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-loading`)}>
            {[0, 1, 2].map((i) => (
              <Card key={i} className="px-20 py-16">
                <div className="flex items-center gap-16">
                  <Skeleton className="size-44 shrink-0 rounded-full" />
                  <div className="flex flex-1 flex-col gap-8">
                    <Skeleton className="h-16 w-1/2" />
                    <Skeleton className="h-12 w-2/3" />
                  </div>
                  <Skeleton className="h-20 w-[72px] rounded-md" />
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* ── Error. ───────────────────────────────────────────────────────────────────────── */}
        {enabled && search.isError && (
          <Card>
            <EmptyState
              testId={`${PREFIX}-error`}
              icon={<Icon icon={Building2} size={20} aria-hidden />}
              title="Couldn't search right now"
              description={
                isGivmoError(search.error)
                  ? search.error.message
                  : 'Something went wrong searching the catalog. Please try again.'
              }
              action={
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  onClick={() => void search.refetch()}
                  testId={`${PREFIX}-error-retry`}
                >
                  Try again
                </Button>
              }
            />
          </Card>
        )}

        {/* ── No matches. ──────────────────────────────────────────────────────────────────── */}
        {showEmpty && (
          <Card>
            <EmptyState
              testId={`${PREFIX}-empty`}
              icon={<Icon icon={Search} size={20} aria-hidden />}
              title="No organizations found"
              description={
                <span>
                  No nonprofits match your search. Try a different name or the organization&rsquo;s
                  EIN. Can&rsquo;t find yours?{' '}
                  {/* EP-FIX7: the in-app `/support` page, not the 404ing `www.givmo.io/support`. This
                      link is shown to callers with no membership yet, so `/support` is exempt from
                      ClaimFirstGate — otherwise it would bounce them back to this very screen. */}
                  <Link to="/support" className="font-w600 text-brand-700 hover:underline">
                    Contact support
                  </Link>
                  .
                </span>
              }
            />
          </Card>
        )}

        {/* ── Results. ─────────────────────────────────────────────────────────────────────── */}
        {enabled && results.length > 0 && (
          <div className="flex flex-col gap-10">
            {/* A count is a comparison aid; with one row there is nothing to compare, so the line is
                pure chrome above the only answer. It appears from two rows up. */}
            {(results.length > 1 || updatingResults) && (
              <div className="flex items-center justify-between gap-8 font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
                {results.length > 1 ? (
                  <span {...testIdProp(`${PREFIX}-count`)}>
                    <span className="tabular-nums">{results.length}</span> results
                  </span>
                ) : (
                  <span />
                )}
                {updatingResults ? (
                  <span {...testIdProp(`${PREFIX}-updating`)}>Updating results…</span>
                ) : null}
              </div>
            )}
            <ul
              className={`flex flex-col gap-8 transition-opacity motion-reduce:transition-none ${
                updatingResults ? 'opacity-50' : ''
              }`}
              {...testIdProp(`${PREFIX}-results`)}
            >
              {results.map((charity) => {
                const einDisplay = formatEin(charity.EIN);
                return (
                  <li key={charity.id} {...testIdProp(`${PREFIX}-result-${charity.id}`)}>
                    {/* ONE control per row. The visible "Continue" is a span, not a nested button:
                        a row that looks clickable everywhere but only commits on a 72px target is
                        the single most common complaint about a picker like this. */}
                    <button
                      type="button"
                      disabled={staleRows}
                      onClick={() => void navigate({ to: wizardPath(charity.id) })}
                      className="group w-full rounded-xl border border-border-subtle bg-surface px-20 py-16 text-left shadow-xs outline-none transition-[border-color,box-shadow] hover:border-brand-300 focus-visible:border-brand-500 focus-visible:shadow-ring disabled:cursor-default disabled:opacity-60 motion-reduce:transition-none"
                      {...testIdProp(`${PREFIX}-select-${charity.id}`)}
                    >
                      <span className="flex items-center gap-16">
                        <Avatar name={charity.name} src={charity.logo_url ?? undefined} size="lg" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-sans text-s16 font-w600 tracking-givmo text-text-strong">
                            {charity.name}
                          </span>
                          <span className="mt-2 flex flex-wrap items-center gap-8 font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
                            {einDisplay ? (
                              <span>
                                EIN <span className="tabular-nums">{einDisplay}</span>
                              </span>
                            ) : null}
                            {/* The bare domain chip said nothing about what the domain WAS. It is
                                the organization's website on record — the thing the instant check
                                compares a work email against — so it says that. */}
                            {charity.organization_domain ? (
                              <span>
                                <span className="text-text-tertiary">Website</span>{' '}
                                {charity.organization_domain}
                              </span>
                            ) : null}
                          </span>
                        </span>
                        <span className="flex shrink-0 items-center gap-6 rounded-md bg-brand-50 px-12 py-6 font-sans text-s13 font-w600 tracking-givmo text-brand-700 transition-colors group-hover:bg-brand-100 motion-reduce:transition-none">
                          Continue
                          <Icon icon={ArrowRight} size={14} aria-hidden />
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {search.hasNextPage ? (
              <div className="flex justify-center">
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  loading={search.isFetchingNextPage}
                  onClick={() => void search.fetchNextPage()}
                  testId={`${PREFIX}-load-more`}
                >
                  Load more
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
